import { useMemo } from 'react';

import { PregnancyCompletionType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { MedicalInformationAttachments } from '../../shared/fields/MedicalInformationAttachments';
import { MedicalInformationDescriptionTextarea } from '../../shared/fields/MedicalInformationDescriptionTextarea';

import {
   GynecologicalInterviewAddEntryFormBean,
   GynecologicalInterviewAddEntryFormChildrenRenderProps,
   GynecologicalInterviewPatientPregnantFormController,
   GynecologicalInterviewPregnancyCompletionFormController,
} from './gynecologicalInterviewAddEntryForm.types';
import {
   GynecologicalInterviewFormPartVisitor,
   renderGynecologicalInterviewFormPart,
} from './gynecologicalInterviewAddEntryForm.utils';
import { GynecologicalInterviewPatientPregnantFormPart } from './GynecologicalInterviewPatientPregnantFormPart';
import { GynecologicalInterviewPregnancyCompletionFormPart } from './GynecologicalInterviewPregnancyCompletionFormPart';
import { GynecologicalInterviewTypeSelect } from './GynecologicalInterviewTypeSelect';

type GynecologicalInterviewAddEntryFormProps = {
   readonly medicalInformationId: number;
   readonly children: (renderProps: GynecologicalInterviewAddEntryFormChildrenRenderProps) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const GynecologicalInterviewAddEntryForm = (props: GynecologicalInterviewAddEntryFormProps): JSX.Element => {
   const {
      register,
      control,
      watch,
      handleSubmit,
      formState,
      sync,
   } = useFormController<GynecologicalInterviewAddEntryFormBean>({
      description: null,
      medicalInformationAttachment: null,
      interviewType: null,
      attachments: null,
   });

   const currentInterviewType = watch('interviewType');

   const gynecologicalInterviewFormPartVisitor: GynecologicalInterviewFormPartVisitor = useMemo(
      () => ({
         visitRegularInterview: () =>
            props.children({
               handleSubmit: handleSubmit,
               formState: formState,
               interviewType: currentInterviewType as 'REGULAR_INTERVIEW' | null,
            }),
         visitPatientPregnantInterview: () => (
            <>
               <GynecologicalInterviewPatientPregnantFormPart
                  register={register as GynecologicalInterviewPatientPregnantFormController['register']}
                  watch={watch as GynecologicalInterviewPatientPregnantFormController['watch']}
               />
               {props.children({
                  handleSubmit: handleSubmit as GynecologicalInterviewPatientPregnantFormController['handleSubmit'],
                  formState: formState,
                  interviewType: 'PATIENT_PREGNANT',
               })}
            </>
         ),
         visitPregnancyCompletionInterview: () => (
            <>
               <GynecologicalInterviewPregnancyCompletionFormPart
                  register={register as GynecologicalInterviewPregnancyCompletionFormController['register']}
               />
               {props.children({
                  handleSubmit: handleSubmit as GynecologicalInterviewPregnancyCompletionFormController['handleSubmit'],
                  formState: formState,
                  interviewType: currentInterviewType as PregnancyCompletionType,
               })}
            </>
         ),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [register, watch, currentInterviewType, formState, handleSubmit, props.children]
   );
   /* eslint-enable */

   return (
      <StyledFlex direction={'column'} gap={'24px'}>
         <GynecologicalInterviewTypeSelect register={register} />
         {renderGynecologicalInterviewFormPart(currentInterviewType, gynecologicalInterviewFormPartVisitor)}
         {currentInterviewType === null ? null : (
            <>
               <MedicalInformationDescriptionTextarea
                  register={register}
                  control={control}
                  required={currentInterviewType === 'REGULAR_INTERVIEW'}
               />
               <MedicalInformationAttachments
                  controller={register('attachments')}
                  medicalInformationId={props.medicalInformationId}
                  sync={sync}
               />
            </>
         )}
      </StyledFlex>
   );
};
